/**
 * Accounting Tags TypeScript type definitions
 *
 * TEMPORARY: These types are derived from the Pact contract and will be replaced
 * by Typelizer-generated serializer types once the backend runs `typelizer:generate`.
 *
 * @see docs/plans/iterations/accounting-tags/contracts/frontend-rails-api.json
 */

// =============================================================================
// Tag Types
// =============================================================================

/**
 * Tag types supported by the system
 */
export type TagType =
  | 'vendor'
  | 'recurring'
  | 'trip'
  | 'project'
  | 'shared'
  | 'tax';

/**
 * Source of how a tag was applied to a transaction
 */
export type TaggingSource = 'rule' | 'manual' | 'import' | 'llm';

/**
 * A tag in the system (base fields)
 */
export interface Tag {
  id: number;
  tagType: TagType;
  value: string;
  displayName: string | null;
  color: string | null;
  description: string | null;
  active: boolean;
  depth: number;
  parentValue: string | null;
}

/**
 * Extended tag with transaction statistics (returned from GET /api/accounting/tags/:id)
 */
export interface TagWithStats extends Tag {
  transactionCount: number;
  totalAmountCents: number;
}

/**
 * A tag on a transaction (includes source and confidence)
 */
export interface TransactionTag {
  id: number;
  tagType: TagType;
  value: string;
  displayName: string;
  color: string | null;
  source: TaggingSource;
  confidence: number;
  taggingId: number;
}

// =============================================================================
// Tagging Types
// =============================================================================

/**
 * A tagging - the relationship between a transaction and a tag
 */
export interface Tagging {
  id: number;
  tagId: number;
  transactionId: number;
  source: TaggingSource;
  confidence: number;
  tag: Pick<Tag, 'id' | 'tagType' | 'value' | 'displayName' | 'color'>;
}

// =============================================================================
// Tagging Rules Types
// =============================================================================

/**
 * A tag reference within a tagging rule
 */
export interface TaggingRuleTag {
  id: number;
  tagType: TagType;
  value: string;
  displayName: string;
  color: string | null;
}

/**
 * An active tagging rule
 */
export interface TaggingRule {
  id: number;
  name: string;
  pattern: string;
  tagId: number;
  tag: TaggingRuleTag;
  priority: number;
  active: boolean;
  matchCount: number;
  lastMatchedAt: string | null;
}

// =============================================================================
// Pending Tagging Rule Types
// =============================================================================

/**
 * Status of a pending tagging rule
 */
export type PendingRuleStatus = 'pending' | 'approved' | 'rejected' | 'auto_approved';

/**
 * Transaction summary within a pending rule
 */
export interface PendingRuleTransaction {
  id: number;
  description: string;
  date: string;
  amountCents: number;
}

/**
 * A pending tagging rule suggestion from LLM
 */
export interface PendingTaggingRule {
  id: number;
  transactionId: number;
  transaction: PendingRuleTransaction;
  suggestedVendor: string;
  suggestedPattern: string | null;
  confidence: number;
  status: PendingRuleStatus;
  llmReasoning: string | null;
  suggestedTag: TaggingRuleTag | null;
  createdAt: string;
}

// =============================================================================
// Report Types
// =============================================================================

/**
 * Vendor spending report row
 */
export interface VendorSpendingRow {
  vendor: string;
  tagId: number;
  tagValue: string;
  color: string | null;
  count: number;
  totalCents: number;
}

/**
 * Vendor spending report response
 */
export interface VendorSpendingReport {
  vendors: VendorSpendingRow[];
  dateRange: {
    startDate: string | null;
    endDate: string | null;
  };
}

/**
 * Recurring expense summary
 */
export interface RecurringSummary {
  frequency: 'monthly' | 'annual' | 'quarterly';
  count: number;
  totalCents: number;
  monthlyEstimateCents: number;
}

/**
 * Recurring expense detail (for detailed dashboard view)
 */
export interface RecurringExpense {
  vendorTagId: number;
  vendorTagValue: string;
  vendorDisplayName: string | null;
  vendorColor: string | null;
  recurringTagId: number;
  recurringTagValue: 'monthly' | 'annual' | 'quarterly';
  recurringDisplayName: string | null;
  recurringColor: string | null;
  lastTransactionDate: string;
  averageAmountCents: number;
  transactionCount: number;
}

/**
 * Recurring expenses report response
 */
export interface RecurringExpensesReport {
  recurring: RecurringSummary[];
  totalMonthlyEstimateCents: number;
}

/**
 * Project cost row
 */
export interface ProjectCostRow {
  project: string;
  tagId: number;
  tagValue: string;
  color: string | null;
  count: number;
  totalCents: number;
}

/**
 * Project costs report response
 */
export interface ProjectCostsReport {
  projects: ProjectCostRow[];
}

/**
 * Tax deduction row
 */
export interface TaxDeductionRow {
  category: string;
  tagId: number;
  tagValue: string;
  color: string | null;
  count: number;
  totalCents: number;
}

/**
 * Tax deductions report response
 */
export interface TaxDeductionsReport {
  taxDeductions: TaxDeductionRow[];
}

// =============================================================================
// API Request Types
// =============================================================================

/**
 * Request body for creating a tag
 */
export interface CreateTagRequest {
  tag: {
    tagType: TagType;
    value: string;
    displayName?: string;
    color?: string;
    description?: string;
  };
}

/**
 * Request body for updating a tag
 */
export interface UpdateTagRequest {
  tag: {
    displayName?: string;
    color?: string;
    description?: string;
    active?: boolean;
  };
}

/**
 * Request body for creating a tagging rule
 */
export interface CreateTaggingRuleRequest {
  taggingRule: {
    name: string;
    pattern: string;
    tagId: number;
    priority?: number;
  };
}

/**
 * Request body for updating a tagging rule
 */
export interface UpdateTaggingRuleRequest {
  taggingRule: {
    name?: string;
    pattern?: string;
    tagId?: number;
    priority?: number;
    active?: boolean;
  };
}

/**
 * Request body for approving a pending rule
 */
export interface ApprovePendingRuleRequest {
  vendorOverride?: string | null;
  patternOverride?: string | null;
}

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Response from listing tags
 */
export interface TagsListResponse {
  tags: Tag[];
}

/**
 * Response from getting a single tag
 */
export interface TagResponse {
  tag: TagWithStats;
}

/**
 * Response from creating/updating a tag
 */
export interface TagMutationResponse {
  tag: Tag;
}

/**
 * Response from listing transaction tags
 */
export interface TransactionTagsResponse {
  tags: TransactionTag[];
}

/**
 * Response from adding a tag to a transaction
 */
export interface AddTagResponse {
  tagging: Tagging;
}

/**
 * Response from applying rules to a transaction
 */
export interface ApplyRulesResponse {
  appliedCount: number;
  tags: Pick<Tag, 'id' | 'tagType' | 'value' | 'displayName'>[];
}

/**
 * Response from listing tagging rules
 */
export interface TaggingRulesListResponse {
  taggingRules: TaggingRule[];
}

/**
 * Response from creating/updating a tagging rule
 */
export interface TaggingRuleMutationResponse {
  taggingRule: TaggingRule;
}

/**
 * Response from listing pending tagging rules
 */
export interface PendingRulesListResponse {
  pendingRules: PendingTaggingRule[];
}

/**
 * Response from approving a pending rule
 */
export interface ApprovePendingRuleResponse {
  success: boolean;
  message: string;
  pendingRule: {
    id: number;
    status: 'approved';
  };
  createdTag: TaggingRuleTag;
  createdRule: {
    id: number;
    name: string;
    pattern: string;
    tagId: number;
  };
}

/**
 * Response from rejecting a pending rule
 */
export interface RejectPendingRuleResponse {
  success: boolean;
  message: string;
  pendingRule: {
    id: number;
    status: 'rejected';
  };
}

/**
 * Standard error response from the API
 */
export interface TagsErrorResponse {
  error: string;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get full tag string from tag type and value
 */
export function getFullTag(tagType: TagType, value: string): string {
  return `${tagType}:${value}`;
}

/**
 * Get full tag string from a tag object
 */
export function getTagString(tag: Pick<Tag, 'tagType' | 'value'>): string {
  return `${tag.tagType}:${tag.value}`;
}

/**
 * Parse full tag string into type and value
 */
export function parseTagString(
  tagString: string
): { tagType: TagType; value: string } | null {
  const parts = tagString.split(':', 2);
  if (parts.length !== 2) return null;
  const tagType = parts[0] as TagType;
  const validTypes: TagType[] = ['vendor', 'recurring', 'trip', 'project', 'shared', 'tax'];
  if (!validTypes.includes(tagType)) return null;
  return { tagType, value: parts[1] };
}

/**
 * Type guard to check if a response is an error response
 */
export function isTagsErrorResponse(response: unknown): response is TagsErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'error' in response &&
    typeof (response as TagsErrorResponse).error === 'string'
  );
}
